import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { DsButtonVariants, Icons } from '@levelaccess/design-system';

import { normalizeToName, normalizeToPersisted } from '@shared/utils/browser-flags.util';
import { REQUIRED_BROWSER_ARGS } from '@shared/constants/chrome-browser-flags';
import { chromeBrowserFlags, defaultChromeBrowserFlags } from '../../../../shared/constants/chrome-browser-flags';
import { CustomValidators } from '../../services/helpers/form-custom-validators';
import { validationError } from '../../constants/form.constants';

enum $fields {
  browserFlags = 'browserFlags',
  modifyBrowserFlags = 'modifyBrowserFlags',
  newBrowserFlag = 'newBrowserFlag',
  newBrowserFlagForm = 'newBrowserFlagForm',
}

const MAX_BROWSER_FLAGS_LENGTH: number = 140;

@Component({
  selector: 'app-scan-custom-browser-flags',
  templateUrl: './scan-custom-browser-flags.component.html',
  styleUrls: ['./scan-custom-browser-flags.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ScanCustomBrowserFlagsComponent {
  @Input()
  public form: UntypedFormGroup;
  @Input()
  public formValidationRequest$: Subject<void>;
  public browserFlagsControlName: string = $fields.browserFlags;
  public modifyBrowserFlagsControlName: string = $fields.modifyBrowserFlags;
  public newBrowserFlagControlName: string = $fields.newBrowserFlag;
  public newBrowserFlagFormName: string = $fields.newBrowserFlagForm;
  public newBrowserFlagForm: UntypedFormGroup;
  public readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  public readonly Icons: typeof Icons = Icons;
  public browserFlagsValidationRequest$: Subject<void> = new Subject<void>();
  public readonly requiredFlagNames: ReadonlySet<string> = new Set(REQUIRED_BROWSER_ARGS.map(normalizeToName));
  public readonly requiredFlagsArray: string[] = REQUIRED_BROWSER_ARGS.map((arg: string) => normalizeToPersisted(arg));

  constructor(private formBuilder: UntypedFormBuilder) {
    this.newBrowserFlagForm = this.formBuilder.group(
      {
        [this.newBrowserFlagControlName]: this.formBuilder.control('', [
          Validators.minLength(1),
          Validators.maxLength(MAX_BROWSER_FLAGS_LENGTH),
          CustomValidators.validateBrowserFlag,
          CustomValidators.required,
        ]),
      },
      { updateOn: 'submit' },
    );
  }

  @Input()
  public set browserFlags(browserFlags: string[] | undefined) {
    if (this.form) {
      const raw: string[] = Array.isArray(browserFlags) ? browserFlags : [...defaultChromeBrowserFlags];
      const filtered = raw.filter((flag: string) => !this.requiredFlagNames.has(normalizeToName(flag)));
      this.form.registerControl(this.browserFlagsControlName, this.formBuilder.control(filtered));
    }
  }

  @Input()
  public set modifyBrowserFlags(modifyBrowserFlags: boolean) {
    if (this.form) {
      this.form.registerControl(this.modifyBrowserFlagsControlName, this.formBuilder.control(Boolean(modifyBrowserFlags)));
    }
  }

  public get modifyBrowserFlags(): AbstractControl | null {
    return this.form.get(this.modifyBrowserFlagsControlName);
  }

  public get browserFlags(): AbstractControl | null {
    return this.form.get(this.browserFlagsControlName);
  }

  public get newBrowserFlag(): AbstractControl | null {
    return this.newBrowserFlagForm.get(this.newBrowserFlagControlName);
  }

  protected addNewBrowserFlag(): void {
    const newBrowserFlagValue = this.newBrowserFlag?.value?.trim() || '';
    this.newBrowserFlagForm.markAllAsTouched();
    this.newBrowserFlag?.updateValueAndValidity();

    if (!this.newBrowserFlag?.valid) {
      this.browserFlagsValidationRequest$.next();
      return;
    }

    const flagName = normalizeToName(newBrowserFlagValue);

    const existingFlags = this.browserFlags?.value || [];
    const isDuplicate = existingFlags.some((existingFlag: string) => {
      const existingFlagName = normalizeToName(existingFlag);
      return existingFlagName === flagName;
    });
    const isReadOnlyFlag = REQUIRED_BROWSER_ARGS.some((required: string) => normalizeToName(required) === flagName);

    if (isDuplicate) {
      this.newBrowserFlag?.setErrors({ [validationError.duplicateBrowserFlag]: true });
      this.browserFlagsValidationRequest$.next();
      return;
    }

    if (isReadOnlyFlag) {
      this.newBrowserFlag?.setErrors({ [validationError.readOnlyBrowserFlag]: true });
      this.browserFlagsValidationRequest$.next();
      return;
    }

    if (!chromeBrowserFlags.has(flagName)) {
      this.newBrowserFlag?.setErrors({ [validationError.invalidBrowserFlag]: true });
      this.browserFlagsValidationRequest$.next();
      return;
    }

    this.browserFlags.setValue([...this.browserFlags.value, newBrowserFlagValue]);
    this.newBrowserFlagForm.reset();
    this.form.markAsDirty();
  }

  public removeBrowserFlag(flag: string): void {
    const flagName = normalizeToName(flag);
    if (this.requiredFlagNames.has(flagName)) {
      return;
    }
    const currentFlags = this.browserFlags.value;
    const updatedFlags = currentFlags.filter((f: string) => f !== flag);
    this.browserFlags.setValue(updatedFlags);
    this.form.markAsDirty();
  }

  public onNewBrowserFlagInput(value: string): void {
    this.newBrowserFlagForm.reset({ [this.newBrowserFlagControlName]: value });
  }
}
